import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { ManagedWindow, closeWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { JPBManager } from "./JPBManager";
import { JPBReferenceProperty, AreaGrouping, YearGrouping, DEFAULT_ASSUMPTIONS, AreaUnit, PriceMode, JPBReportData, JPBReportSelectedLayer, JPBAssumptions } from "./JPBState";
import { JPBHistogram } from "./JPBHistogram";
import { JPBTable, JPBTableRef } from "./JPBTable";
import { JPBSearchArea } from "./JPBSearchArea";
import { JPBStatistics } from "./JPBStatistics";
import { JPBPolygonAnalyzer } from "./JPBPolygonAnalyzer";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { getCheckedItemsFromCurrentAssumptions } from "./JPBFunc";
import { MapitStateContext, actionSetMapInteractionState, MapInteractionState } from "src/states/MapitState";
import type { GeoJSONSource } from "maplibre-gl";
import { feature } from "@turf/helpers";
import { bboxPolygon, difference, featureCollection, buffer } from "@turf/turf";
import "../../css/propertyInfoStyle.css";
import { LayerVariabilityType, LayerType } from "src/common/managers/Types";
import { setupJPBMapLayers, removeJPBMapLayers, updateFeatureStates, SOURCE_ID, SOURCE_ID_POLY, LAYER_ID, LAYER_ID_BUILDING, LAYER_ID_POLY, LAYER_ID_FARMLAND, LAYER_ID_JORDBUNDSKORT, SOURCE_ID_JORDBUNDSKORT, highlightPolygonOnMap, createJordbundskortGeoJson } from "./JPBMapLayers";
import { Spinner } from "react-bootstrap";
import { handleGenerateJPBRapport } from "./JPBRepportGen";
import { renderJPBOverviewImage, renderJPBHeatmapImage, renderJPBOverviewWithJordbundImage, renderJPBHistogramImage } from "./JPBMapSnapshot";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { Feature } from "src/states/ApplicationState";

const MASK_SOURCE_ID = 'jpb-search-area-mask-source';
const MASK_LAYER_ID = 'jpb-search-area-mask';

export function JPBWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);

   // MARK: State
   const [referenceProperties, setReferenceProperties] = useState<JPBReferenceProperty[]>([]);
   const [loading, setLoading] = useState<boolean>(false);
   const [error, setError] = useState<string | null>(null);
   const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
   const [bufferGeometry, setBufferGeometry] = useState<any>(null);
   const [areaGrouping, setAreaGrouping] = useState<AreaGrouping>(AreaGrouping.All);
   const [yearGrouping, setYearGrouping] = useState<YearGrouping>(YearGrouping.ThreeYears);
   const [hideUnselected, setHideUnselected] = useState<'none' | 'unchecked' | 'locked'>('none');
   const [customPriceAssumptions, setCustomPriceAssumptions] = useState<{ pricePerMeterMin?: number, pricePerMeterMax?: number, excludeBuildings?: boolean } | undefined>(undefined);
   const [systemPriceAssumptions, setSystemPriceAssumptions] = useState<{ pricePerMeterMin: number, pricePerMeterMax: number } | undefined>(undefined);
   const [selectedLayer, setSelectedLayer] = useState<number | undefined>(undefined);
   const [areaUnit, setAreaUnit] = useState<AreaUnit>(() => {
      const saved = localStorage.getItem("jpb.areaUnit");
      return (saved as AreaUnit) || AreaUnit.SquareMeter;
   });
   const [priceMode, setPriceMode] = useState<PriceMode>(() => {
      const saved = localStorage.getItem("jpb.priceMode");
      return (saved as PriceMode) || PriceMode.Standard;
   });
   const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
   const tableRef = useRef<JPBTableRef>(null);
   const tableScrollAnchorRef = useRef<HTMLDivElement>(null);
   const histogramRef = useRef<HTMLDivElement>(null);
   const modalBodyRef = useRef<HTMLDivElement>(null);
   const checkedItemsRef = useRef<Set<string>>(checkedItems);
   const originalIconImagesRef = useRef<{ [layerId: string]: any }>({}); // Store original icon images
   const highlightCleanupRef = useRef<(() => void) | null>(null); // Store cleanup function for polygon highlight
   checkedItemsRef.current = checkedItems;

   useEffect(() => {
      mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Override));
      return () => {
         mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
         // Clean up any active highlight when component unmounts
         if (highlightCleanupRef.current) {
            highlightCleanupRef.current();
            highlightCleanupRef.current = null;
         }
      };
   }, []);

   // MARK: Handlers
   const lockedBuildingDocumentIds = useMemo(() => {
      const assumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS);
      const currentAssumptions = {
         ...DEFAULT_ASSUMPTIONS,
         ...assumptions,
         ...customPriceAssumptions,
      } as any;

      const thresholdM2 = currentAssumptions.buildingAreaOkM2 ?? DEFAULT_ASSUMPTIONS.buildingAreaOkM2 ?? 0;
      const useAgriculturalArea =
         currentAssumptions.buildingAreaOkUseAgriculturalArea ?? DEFAULT_ASSUMPTIONS.buildingAreaOkUseAgriculturalArea ?? false;

      const locked = new Set<string>();
      for (const prop of referenceProperties) {
         const hasBuildingValue =
            (typeof prop.prisDriftsbygning === "number" && Number.isFinite(prop.prisDriftsbygning) && prop.prisDriftsbygning > 0) ||
            (typeof prop.prisStuehus === "number" && Number.isFinite(prop.prisStuehus) && prop.prisStuehus > 0);
         if (!hasBuildingValue) continue;

         const markOverlapPct = prop.overlapArea?.markOverlap ?? 0;
         const buildingAreaM2 = useAgriculturalArea ? prop.area * (markOverlapPct / 100) : prop.area;
         const isLocked = thresholdM2 > 0 && buildingAreaM2 < thresholdM2;
         if (isLocked) {
            locked.add(prop.documentID);
         }
      }
      return locked;
   }, [referenceProperties, customPriceAssumptions]);

   const sanitizeCheckedItems = (items: Set<string>) => {
      if (lockedBuildingDocumentIds.size === 0) return items;
      const sanitized = new Set(items);
      lockedBuildingDocumentIds.forEach((id) => sanitized.delete(id));
      return sanitized;
   };

   const updateCheckedItems = (newCheckedItems: Set<string>) => {
      const sanitized = sanitizeCheckedItems(newCheckedItems);
      setCheckedItems(sanitized);

      const map = mapitState.map?.getMapPop();
      if (map && map.getSource(SOURCE_ID)) {
         referenceProperties.forEach(prop => {
            map.setFeatureState(
               { source: SOURCE_ID, id: prop.documentID },
               { checked: sanitized.has(prop.documentID) }
            );
            map.setFeatureState(
               { source: SOURCE_ID_POLY, id: prop.documentID },
               { checked: sanitized.has(prop.documentID) }
            );
         });
      }
   };

   const handleRowClick = (property: JPBReferenceProperty) => {
      if (!mapitState.map) {
         console.warn('handleRowClick: mapitState.map is not available');
         return;
      }
      const map = mapitState.map.getMapPop();
      if (!map) {
         console.warn('handleRowClick: map is not available');
         return;
      }

      // Clear any existing highlight
      if (highlightCleanupRef.current) {
         highlightCleanupRef.current();
         highlightCleanupRef.current = null;
      }

      if (property?.geoshape) {
         try {
            const geoshapeFeature = feature(property.geoshape);
            mapitState.map.zoomToDataAvoidingWindows(geoshapeFeature, 25);

            // Highlight the polygon with bright green outline
            highlightCleanupRef.current = highlightPolygonOnMap(
               map,
               property.geoshape,
               property.documentID,
               5000 // 5 seconds, matching table row highlight duration
            );
         } catch (error) {
            console.error('handleRowClick: error creating feature or zooming', error);
         }
      } else {
         try {
            const pointFeature = feature({
               type: "Point",
               coordinates: [property.coordinates.lng, property.coordinates.lat]
            });
            const bufferedFeature = buffer(pointFeature, 0.5, { units: 'kilometers' });
            if (bufferedFeature) {
               mapitState.map.zoomToDataAvoidingWindows(bufferedFeature, 25);

               // Highlight the buffered circle with bright green outline
               highlightCleanupRef.current = highlightPolygonOnMap(
                  map,
                  bufferedFeature.geometry,
                  property.documentID,
                  5000 // 5 seconds, matching table row highlight duration
               );
            } else {
               console.warn('handleRowClick: bufferedFeature is null');
            }
         } catch (error) {
            console.error('handleRowClick: error creating point or zooming', error);
         }
      }
   };

   const handleExportReport = async () => {
      setIsGeneratingReport(true);
      try {
         const { assumptions } = getCheckedItemsFromCurrentAssumptions(
            referenceProperties,
            areaGrouping,
            yearGrouping,
            customPriceAssumptions,
            priceMode
         );

         let selectedLayerInfo: JPBReportSelectedLayer | undefined;
         if (selectedLayer !== undefined) {
            const layer = mapitState.layers[selectedLayer];
            const name = (layer as any)?.layer?.label ?? (layer as any)?.layer?.name ?? String(selectedLayer);
            selectedLayerInfo = { id: selectedLayer, name };
         }

         let overviewImage: Uint8Array | undefined;
         let heatmapImage: Uint8Array | undefined;
         let overviewWithJordbundImage: Uint8Array | undefined;
         let histogramImage: Uint8Array | undefined;
         try {
            if (referenceProperties.length > 0 && (mapitState as any).mapInfo?.mapStyle) {
               // Generate overview image with styled points
               overviewImage = await renderJPBOverviewImage({
                  mapStyle: (mapitState as any).mapInfo.mapStyle,
                  referenceProperties,
                  checkedDocumentIds: Array.from(checkedItems),
                  bufferGeometry
               });

               // Generate heatmap image
               heatmapImage = await renderJPBHeatmapImage({
                  mapStyle: (mapitState as any).mapInfo.mapStyle,
                  referenceProperties,
                  checkedDocumentIds: Array.from(checkedItems),
                  bufferGeometry
               });

               // Generate overview image with jordbund layer
               overviewWithJordbundImage = await renderJPBOverviewWithJordbundImage({
                  mapStyle: (mapitState as any).mapInfo.mapStyle,
                  referenceProperties,
                  checkedDocumentIds: Array.from(checkedItems),
                  bufferGeometry
               });
            }

            // Generate histogram image
            if (filteredDataForHistogram.length > 0 && histogramRef.current) {
               histogramImage = await renderJPBHistogramImage(histogramRef.current);
            }
         } catch (e) {
            console.error("Failed to capture JPB images", e);
         }

         const reportData: JPBReportData = {
            yearGrouping,
            areaGrouping,
            assumptions,
            referenceProperties,
            checkedDocumentIds: Array.from(checkedItems),
            selectedLayer: selectedLayerInfo,
            overviewImage,
            heatmapImage,
            overviewWithJordbundImage,
            histogramImage
         };

         await handleGenerateJPBRapport(reportData);
      } finally {
         setIsGeneratingReport(false);
      }
   };


   // MARK: Computed
   const selectableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
   }, [mapitState.layers]);

   const filteredDataForHistogram = useMemo(() => {
      if (referenceProperties.length === 0) {
         return [];
      }
      return referenceProperties.filter(p => checkedItems.has(p.documentID));
   }, [referenceProperties, checkedItems]);


   const statsCheckedItemsStandard = useMemo(() => {
      if (referenceProperties.length === 0) return new Set<string>();
      return getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         yearGrouping,
         customPriceAssumptions,
         PriceMode.Standard
      ).checkedItems;
   }, [referenceProperties, areaGrouping, yearGrouping, customPriceAssumptions]);

   const statsCheckedItemsLandbrug = useMemo(() => {
      if (referenceProperties.length === 0) return new Set<string>();
      return getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         yearGrouping,
         customPriceAssumptions,
         PriceMode.Landbrug
      ).checkedItems;
   }, [referenceProperties, areaGrouping, yearGrouping, customPriceAssumptions]);


   // MARK: Data Fetch
   useEffect(() => {
      if (!mapitState.map) return;
      const abortController = new AbortController();

      async function fetchData() {
         if (!bufferGeometry) {
            setReferenceProperties([]);
            setCheckedItems(new Set());
            setLoading(false);
            return;
         }

         setLoading(true);
         setError(null);
         try {
            const polygonCoordinates = JPBManager.extractPolygonCoordinates(bufferGeometry);

            if (!polygonCoordinates) {
               setError(Localization.getText("Could not extract polygon coordinates from buffer"));
               setLoading(false);
               return;
            }

            const properties = await JPBManager.fetchReferenceProperties({ polygonCoordinates, yearGrouping }, abortController.signal);
            setReferenceProperties(properties);

            const systemAssumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS);
            setSystemPriceAssumptions({
               pricePerMeterMin: systemAssumptions.pricePerMeterMin,
               pricePerMeterMax: systemAssumptions.pricePerMeterMax
            });

            if (customPriceAssumptions === undefined) {
               setCustomPriceAssumptions({
                  pricePerMeterMin: systemAssumptions.pricePerMeterMin,
                  pricePerMeterMax: systemAssumptions.pricePerMeterMax
               });
            }

            const { checkedItems: initialCheckedItems } = getCheckedItemsFromCurrentAssumptions(
               properties,
               areaGrouping,
               yearGrouping,
               customPriceAssumptions,
               priceMode
            );
            setCheckedItems(initialCheckedItems);
         } catch (err: any) {
            if (abortController.signal.aborted) return;
            console.error("Got error searching: ", err);
            setError(err.message || String(err));
         } finally {
            if (!abortController.signal.aborted) {
               setLoading(false);
            }
         }
      }

      fetchData();

      return () => {
         abortController.abort();
      };
   }, [bufferGeometry, yearGrouping]);

   // Update checked items when areaGrouping or yearGrouping changes
   useEffect(() => {
      if (referenceProperties.length === 0) return;
      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         yearGrouping,
         customPriceAssumptions,
         priceMode
      );
      updateCheckedItems(newCheckedItems);
   }, [areaGrouping, yearGrouping, referenceProperties, customPriceAssumptions, priceMode]);

   // Reset hideUnselected when areaUnit or priceMode changes
   useEffect(() => {
      setHideUnselected('none');
   }, [areaUnit, priceMode]);

   // MARK: Price Handlers
   const handlePriceRangeChange = (min: number, max: number) => {
      setCustomPriceAssumptions({
         pricePerMeterMin: min,
         pricePerMeterMax: max
      });

      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         yearGrouping,
         { pricePerMeterMin: min, pricePerMeterMax: max },
         priceMode
      );
      updateCheckedItems(newCheckedItems);
   };

   const handleAreaUnitChange = (unit: AreaUnit) => {
      setAreaUnit(unit);
      localStorage.setItem("jpb.areaUnit", unit);
   };

   const handlePriceModeChange = (mode: PriceMode) => {
      setPriceMode(mode);
      localStorage.setItem("jpb.priceMode", mode);
   };

   const handleCustomAssumptionsChange = (newAssumptions: Partial<JPBAssumptions>) => {
      setCustomPriceAssumptions(prev => ({
         ...prev,
         ...newAssumptions
      }));
   };

   const handleResetToSystemAssumptions = () => {
      if (!systemPriceAssumptions) return;

      setCustomPriceAssumptions({
         pricePerMeterMin: systemPriceAssumptions.pricePerMeterMin,
         pricePerMeterMax: systemPriceAssumptions.pricePerMeterMax,
         excludeBuildings: false
      });

      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         yearGrouping,
         {
            pricePerMeterMin: systemPriceAssumptions.pricePerMeterMin,
            pricePerMeterMax: systemPriceAssumptions.pricePerMeterMax,
            excludeBuildings: false
         },
         priceMode
      );
      updateCheckedItems(newCheckedItems);
   };

   // MARK: Map Layers
   useEffect(() => {
      if (!mapitState.map || referenceProperties.length === 0) {
         removeJPBMapLayers(mapitState.map?.getMapPop());
         return;
      }

      const map = mapitState.map.getMapPop();
      if (!map) return;

      const cleanup = setupJPBMapLayers(
         map,
         referenceProperties,
         checkedItemsRef.current,
         priceMode,
         areaUnit,
         {
            onFeatureClick: (documentID: string, ctrlKey: boolean) => {
               if (!ctrlKey) {
                  if (tableRef.current) {
                     // The table lives at the bottom of the modal; make sure the modal body scrolls down to it first.
                     tableScrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                     tableRef.current.scrollToRow(documentID);
                  } else {
                     console.warn('tableRef.current is null');
                  }
               } else {
                  if (lockedBuildingDocumentIds.has(documentID)) {
                     return;
                  }
                  const featureState = map.getFeatureState({ source: SOURCE_ID, id: documentID });
                  const currentChecked = featureState?.checked === true;
                  const newChecked = !currentChecked;

                  const newCheckedItems = new Set(checkedItemsRef.current);
                  if (newChecked) {
                     newCheckedItems.add(documentID);
                  } else {
                     newCheckedItems.delete(documentID);
                  }

                  updateCheckedItems(newCheckedItems);
               }
            }
         }
      );

      return cleanup;
   }, [mapitState.map, referenceProperties, priceMode, areaUnit]);

   // Update feature states when checkedItems changes
   useEffect(() => {
      if (!mapitState.map || referenceProperties.length === 0) return;
      const map = mapitState.map.getMapPop();
      if (!map || !map.getSource(SOURCE_ID)) return;

      updateFeatureStates(map, referenceProperties, checkedItems);
   }, [mapitState.map, checkedItems, referenceProperties]);

   useEffect(() => {
      if (!mapitState.map || referenceProperties.length === 0) return;
      const map = mapitState.map.getMapPop();
      if (!map) return;


      if (map.getLayer(LAYER_ID)) {
         map.setPaintProperty(LAYER_ID, 'circle-stroke-opacity',
            hideUnselected === 'unchecked'
               ? ['case', ['to-boolean', ['feature-state', 'checked']], 1, 0]
               : hideUnselected === 'locked'
                  ? ['case', ['in', ['get', 'documentID'], ['literal', Array.from(lockedBuildingDocumentIds)]], 0, 1]
                  : 1
         );
         map.setPaintProperty(LAYER_ID, 'circle-opacity',
            hideUnselected === 'unchecked'
               ? ['case', ['to-boolean', ['feature-state', 'checked']], 1, 0]
               : hideUnselected === 'locked'
                  ? ['case', ['in', ['get', 'documentID'], ['literal', Array.from(lockedBuildingDocumentIds)]], 0, 1]
                  : 1
         );
      }

      if (map.getLayer(LAYER_ID_POLY)) {
         map.setPaintProperty(
            LAYER_ID_POLY,
            'fill-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 14, ['case', ['to-boolean', ['feature-state', 'checked']], 0.5, 0]]
               : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.5]
         );
      }

      if (map.getLayer(LAYER_ID_POLY + "-outline")) {
         map.setPaintProperty(
            LAYER_ID_POLY + "-outline",
            'line-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 12.5, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 13.5, ['case', ['to-boolean', ['feature-state', 'checked']], 1, 0]]
               : ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1]
         );
      }

      if (map.getLayer(LAYER_ID_FARMLAND)) {
         map.setPaintProperty(
            LAYER_ID_FARMLAND,
            'fill-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 14, ['case', ['to-boolean', ['feature-state', 'checked']], 0.6, 0]]
               : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6]
         );
      }

      if (map.getLayer(LAYER_ID_FARMLAND + "-outline")) {
         map.setPaintProperty(
            LAYER_ID_FARMLAND + "-outline",
            'line-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 12.5, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 13.5, ['case', ['to-boolean', ['feature-state', 'checked']], 0.8, 0]]
               : ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 0.8]
         );
      }

      if (map.getLayer(LAYER_ID_JORDBUNDSKORT) && map.getSource(SOURCE_ID_JORDBUNDSKORT)) {
         const jordbundskortSource = map.getSource(SOURCE_ID_JORDBUNDSKORT) as any;
         if (jordbundskortSource && jordbundskortSource.type === 'geojson') {
            const updatedJordbundskortGeoJson = createJordbundskortGeoJson(referenceProperties, checkedItems, lockedBuildingDocumentIds);
            jordbundskortSource.setData(updatedJordbundskortGeoJson);
         }
         map.setPaintProperty(
            LAYER_ID_JORDBUNDSKORT,
            'fill-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['get', 'checked']], 0, 0], 14, ['case', ['to-boolean', ['get', 'checked']], 0.4, 0]]
               : hideUnselected === 'locked'
                  ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['get', 'locked']], 0, 0], 14, ['case', ['to-boolean', ['get', 'locked']], 0, 0.4]]
                  : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.4]
         );
      }

      const checkedIds = Array.from(checkedItems);
      const lockedIds = Array.from(lockedBuildingDocumentIds);

      const showOnlyCheckedFilter: any =
         checkedIds.length > 0 ? ['in', ['get', 'documentID'], ['literal', checkedIds]] : ['==', ['get', 'documentID'], '__none__'];

      const hideLockedFilter: any =
         lockedIds.length > 0 ? ['!', ['in', ['get', 'documentID'], ['literal', lockedIds]]] : null;

      const applyFilter = (layerId: string, baseFilter: any = null) => {
         if (!map.getLayer(layerId)) return;
         if (hideUnselected === 'none') {
            map.setFilter(layerId, baseFilter);
            return;
         }

         let activeFilter: any = null;
         if (hideUnselected === 'unchecked') {
            activeFilter = showOnlyCheckedFilter;
         } else if (hideUnselected === 'locked') {
            activeFilter = hideLockedFilter;
         }

         if (activeFilter) {
            map.setFilter(layerId, baseFilter ? ['all', baseFilter, activeFilter] : activeFilter);
         } else {
            map.setFilter(layerId, baseFilter);
         }
      };

      applyFilter(LAYER_ID);
      // Building layer needs to preserve its hasBuilding filter
      applyFilter(LAYER_ID_BUILDING, ['==', ['get', 'hasBuilding'], true]);
      applyFilter(LAYER_ID_POLY);
      applyFilter(LAYER_ID_POLY + "-outline");
      applyFilter(LAYER_ID_FARMLAND);
      applyFilter(LAYER_ID_FARMLAND + "-outline");
      // Jordbundskort layer should not be filtered - it uses paint properties for visibility
   }, [mapitState.map, hideUnselected, referenceProperties.length, checkedItems, lockedBuildingDocumentIds]);

   // MARK: Mask Layer
   useEffect(() => {
      if (!mapitState.map) return;

      const map = mapitState.map.getMapPop();
      if (!map) return;

      if (!map.getSource(MASK_SOURCE_ID)) {
         map.addSource(MASK_SOURCE_ID, {
            type: 'geojson',
            data: {
               type: 'FeatureCollection',
               features: []
            }
         });
      }

      if (!map.getLayer(MASK_LAYER_ID)) {
         const maskLayerSpec: any = {
            id: MASK_LAYER_ID,
            type: 'fill',
            source: MASK_SOURCE_ID,
            paint: {
               'fill-color': '#000000',
               'fill-opacity': 0.15
            }
         };
         if (map.getLayer('jpb-search-area-outline')) {
            map.addLayer(maskLayerSpec, 'jpb-search-area-outline');
         } else {
            map.addLayer(maskLayerSpec);
         }
      }

      const updateMask = () => {
         if (!bufferGeometry) {
            const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
            if (source) {
               source.setData({
                  type: 'FeatureCollection',
                  features: []
               } as any);
            }
            return;
         }

         try {
            const worldBbox: [number, number, number, number] = [-180, -90, 180, 90];
            const worldPolygon = bboxPolygon(worldBbox);

            let bufferFeature = bufferGeometry;
            if (bufferGeometry.type === 'FeatureCollection' && bufferGeometry.features && bufferGeometry.features.length > 0) {
               bufferFeature = bufferGeometry.features[0];
            } else if (bufferGeometry.type === 'Feature') {
               bufferFeature = bufferGeometry;
            } else if (bufferGeometry.type === 'Polygon' || bufferGeometry.type === 'MultiPolygon') {
               bufferFeature = {
                  type: 'Feature',
                  geometry: bufferGeometry,
                  properties: {}
               };
            }

            const maskGeometry = difference(featureCollection([worldPolygon, bufferFeature]));

            if (maskGeometry) {
               const maskFeatureCollection = {
                  type: 'FeatureCollection',
                  features: [maskGeometry]
               };
               const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
               if (source) {
                  source.setData(maskFeatureCollection as any);
               }
            } else {
               const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
               if (source) {
                  source.setData({
                     type: 'FeatureCollection',
                     features: []
                  } as any);
               }
            }
         } catch (error) {
            console.error("Error creating mask layer:", error);
            const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
            if (source) {
               source.setData({
                  type: 'FeatureCollection',
                  features: []
               } as any);
            }
         }
      };

      updateMask();

      return () => {
         if (map.getLayer(MASK_LAYER_ID)) map.removeLayer(MASK_LAYER_ID);
         if (map.getSource(MASK_SOURCE_ID)) map.removeSource(MASK_SOURCE_ID);
      };
   }, [mapitState.map, bufferGeometry]);

   // MARK: Pin Color Change
   useEffect(() => {
      if (!mapitState.map || selectedLayer === undefined) {
         const map = mapitState.map?.getMapPop();
         if (map) {
            Object.keys(originalIconImagesRef.current).forEach(layerId => {
               const originalIconImage = originalIconImagesRef.current[layerId];
               if (originalIconImage !== undefined && map.getLayer(layerId)) {
                  map.setLayoutProperty(layerId, 'icon-image', originalIconImage);
               }
            });
            originalIconImagesRef.current = {};
         }
         return;
      }

      const layerInfo = mapitState.layers[selectedLayer];
      if (!layerInfo || !layerInfo.handle) return;

      const isPointLayer = layerInfo.type === LayerType.GeoJSON_Point ||
         layerInfo.type === LayerType.GeoJSON_PointRadius;

      if (!isPointLayer) {
         const map = mapitState.map?.getMapPop();
         if (map) {
            Object.keys(originalIconImagesRef.current).forEach(layerId => {
               const originalIconImage = originalIconImagesRef.current[layerId];
               if (originalIconImage !== undefined && map.getLayer(layerId)) {
                  map.setLayoutProperty(layerId, 'icon-image', originalIconImage);
               }
            });
            originalIconImagesRef.current = {};
         }
         return;
      }

      const map = mapitState.map.getMapPop();
      if (!map) return;

      const layerHandle = layerInfo.handle;
      const layerIds = layerHandle.getLayerIds();
      const pinPrefix = SettingsManager.getSystemSetting("pinPrefix", "mit-pin");

      layerIds.forEach(layerId => {
         const layer = map.getLayer(layerId);
         if (layer && layer.type === 'symbol') {
            if (!(layerId in originalIconImagesRef.current)) {
               const currentIconImage = map.getLayoutProperty(layerId, 'icon-image');
               originalIconImagesRef.current[layerId] = currentIconImage;
            }
            const newIconImage = `${pinPrefix}-#d6c251`;
            map.setLayoutProperty(layerId, 'icon-image', newIconImage);
         }
      });

      return () => {
         const cleanupMap = mapitState.map?.getMapPop();
         if (cleanupMap) {
            Object.keys(originalIconImagesRef.current).forEach(layerId => {
               const originalIconImage = originalIconImagesRef.current[layerId];
               if (originalIconImage !== undefined && cleanupMap.getLayer(layerId)) {
                  cleanupMap.setLayoutProperty(layerId, 'icon-image', originalIconImage);
               }
            });
            originalIconImagesRef.current = {};
         }
      };
   }, [mapitState.map, selectedLayer, mapitState.layers]);
   // MARK: RETURN
   return (
      <AdvancedDragModal
         title={Localization.getText("Land Price Calculator")}
         window={props.managedWindow}
         allowUserMiniMize={true}
         allowFullscreen={true}
         className="JPBModule"
         // Default: dock to the right, tall window (so it doesn't start tiny and rely on content growth)
         PosDefault={{
            top: "12px",
            left: "calc(100% - 12px)",
            transform: "translateX(-100%)",
            width: "900px",

         }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}

         subHeaderContent={
            <JPBSearchArea
               selectableLayers={selectableLayers}
               onBufferGeometryChange={setBufferGeometry}
               onSelectedLayerChange={setSelectedLayer}
               propertiesCount={referenceProperties.length}
               areaUnit={areaUnit}
               onAreaUnitChange={handleAreaUnitChange}
               priceMode={priceMode}
               onPriceModeChange={handlePriceModeChange}
               onExportReport={handleExportReport}
               isGeneratingReport={isGeneratingReport}
            />
         }
         onClose={() => {
            windowDispatch(closeWindow(props.managedWindow.id));
         }}
      >
         <ADModalBody>
            {/* Loading overlay */}
            {loading && (
               <div className="jpb-window-loading-overlay">
                  <div className="jpb-window-loading-content">
                     <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
                     <p className="jpb-window-loading-text">
                        {Localization.getText("Fetching reference transactions...")}
                     </p>
                  </div>
               </div>
            )}
            <div ref={modalBodyRef} style={{ padding: "12px 8px", position: "relative", maxHeight: "calc(100vh - 130px)", overflowY: "auto" }}>

               {!bufferGeometry && (
                  <p>{Localization.getText("Choose a layer and set the buffer to search for reference transactions")}</p>
               )}

               {error && (
                  <div className="PropertyWarning">
                     <p>{Localization.getText("Error fetching data:")} {error}</p>
                  </div>
               )}

               {!loading && !error && bufferGeometry && referenceProperties.length === 0 && (
                  <p>{Localization.getText("No reference transactions found")}</p>
               )}
               {bufferGeometry && (
                  <div ref={histogramRef}>
                     <JPBHistogram
                        data={filteredDataForHistogram}
                        allData={referenceProperties}
                        currentMin={customPriceAssumptions?.pricePerMeterMin}
                        currentMax={customPriceAssumptions?.pricePerMeterMax}
                        onRangeChange={handlePriceRangeChange}
                        onResetToSystem={handleResetToSystemAssumptions}
                        areaUnit={areaUnit}
                        priceMode={priceMode}
                     />
                  </div>
               )}
               {referenceProperties.length > 1 && checkedItems.size > 1 && (
                  <JPBStatistics
                     data={referenceProperties}
                     areaUnit={areaUnit}
                     priceMode={priceMode}
                     checkedItems={checkedItems}
                     yearGrouping={yearGrouping}
                  />
               )}
               {referenceProperties.length > 0 && (
                  <>
                     <div ref={tableScrollAnchorRef} />
                     <JPBTable
                        ref={tableRef}
                        data={referenceProperties}
                        checkedItems={checkedItems}
                        onCheckedItemsChange={updateCheckedItems}
                        onRowClick={handleRowClick}
                        onAreaGroupingChange={setAreaGrouping}
                        areaGrouping={areaGrouping}
                        onYearGroupingChange={setYearGrouping}
                        yearGrouping={yearGrouping}
                        hideUnselected={hideUnselected}
                        onHideUnselectedChange={setHideUnselected}
                        customAssumptions={customPriceAssumptions}
                        onCustomAssumptionsChange={handleCustomAssumptionsChange}
                        areaUnit={areaUnit}
                        priceMode={priceMode}
                        portalRoot={modalBodyRef.current}
                     />
                  </>
               )}
               {referenceProperties.length > 0 && (
                  <ProtectedFeature feature={Feature.JPBSimulator} contentsIfNoAccess={<></>}>
                     <JPBPolygonAnalyzer
                        referenceProperties={referenceProperties}
                        checkedItems={checkedItems}
                        areaUnit={areaUnit}
                     />
                  </ProtectedFeature>
               )}
            </div>
         </ADModalBody>
      </AdvancedDragModal>
   );
}


export default JPBWindow;
